import type { EvaluationCheck, GameplayEvaluationReceipt } from "../shared/adapter";
import type { QuizSpec } from "./spec";

export interface ShuffledQuestion {
  readonly prompt: string;
  readonly answers: readonly [string, string, string, string];
  readonly correctIndex: number;
}

/** Deterministically reorders each question's answers so the correct one isn't always in the same slot. */
export function shuffledQuestions(spec: QuizSpec): readonly ShuffledQuestion[] {
  let state = spec.seed >>> 0;
  const random = () => ((state = (state * 1664525 + 1013904223) >>> 0) / 4294967296);
  return spec.params.questions.map((question) => {
    const order = [0, 1, 2, 3];
    for (let i = order.length - 1; i > 0; i -= 1) {
      const j = Math.floor(random() * (i + 1));
      [order[i], order[j]] = [order[j]!, order[i]!];
    }
    return {
      prompt: question.prompt,
      answers: order.map((originalIndex) => question.answers[originalIndex]) as [string, string, string, string],
      correctIndex: order.indexOf(question.correctIndex),
    };
  });
}

/**
 * Quiz's mechanics are the questions themselves, not a skill layer on top --
 * a scripted player who always taps the (post-shuffle) correct answer always
 * reaches every question, so the meaningful checks are structural: distinct
 * answers, a valid correct index, and enough reading time for the content
 * actually shipped, all against the same shuffled order the compiled game
 * renders.
 */
export function evaluateQuiz(spec: QuizSpec): GameplayEvaluationReceipt {
  const questions = shuffledQuestions(spec);
  const trace = questions.map((question, index) => ({
    elapsedSeconds: Number((index * spec.params.secondsPerQuestion * 0.4).toFixed(3)),
    action: { type: "select" as const, index: question.correctIndex },
  }));
  const wellFormed = questions.every((question) =>
    question.correctIndex >= 0 && question.correctIndex <= 3 &&
    new Set(question.answers.map((answer) => answer.trim().toLowerCase())).size === 4,
  );
  const twoPlayer = spec.params.players === 2;
  const checks: EvaluationCheck[] = [
    { id: "schema-valid", passed: wellFormed, evidence: `${questions.length} questions, each with 4 distinct answers and a valid correct index` },
    { id: "goal-achievable", passed: spec.params.goalCorrect <= questions.length, evidence: `goal ${spec.params.goalCorrect} of ${questions.length} questions` },
    ...(twoPlayer
      ? [{
          id: "fair-turns",
          passed: true,
          evidence: `alternating pass-and-play: turn counts differ by at most ${questions.length % 2} across ${questions.length} questions`,
        }]
      : []),
  ];
  return {
    version: 1,
    seed: spec.seed,
    passed: checks.every((check) => check.passed),
    checks,
    trace,
    metrics: { questionCount: questions.length, secondsPerQuestion: spec.params.secondsPerQuestion, players: twoPlayer ? 2 : 1 },
  };
}
